/**
 * Admin preview / edit for 基本情報 / 家族情報 — full structured view with admin save + audit.
 */
import React, { useCallback, useEffect, useState } from 'react';
import type { ChatFlowProfileSectionItem } from '../../../../services/chatFlowApi';
import {
  fetchAdminFamilyInfo,
  fetchAdminGeneralProfile,
  fetchAdminSignaturePreviewUrl,
  updateAdminFamilyInfo,
  updateAdminGeneralProfile,
  type AdminFamilyProfileResponse,
  type AdminGeneralProfileResponse,
  type GeneralProfileValues,
} from '../../../../services/adminProfileSectionApi';
import type { FamilyMemberApi } from '../../../employee/fillingForms/chat/family/familyInfoTypes';
import approveIcon from '../../../../assests/icons/approved.png';
import rejectIcon from '../../../../assests/icons/rejected.png';
import './DocumentPreview.css';
import './ProfileSectionPreview.css';

export interface ProfileSectionPreviewProps {
  section: ChatFlowProfileSectionItem;
  employeeEmail: string;
  employeeName?: string;
  invitationId: string;
  readOnly?: boolean;
  onClose: () => void;
  showResubmitModal: boolean;
  resubmitReason: string;
  onResubmitReasonChange: (value: string) => void;
  onOpenResubmitModal: () => void;
  onCloseResubmitModal: () => void;
  onApprove: () => void | Promise<void>;
  onSaveResubmit: () => void | Promise<void>;
}

type LoadState = 'loading' | 'ready' | 'error';
type ViewMode = 'view' | 'edit';

function emptyGeneralValues(): GeneralProfileValues {
  return {
    familyName: '',
    firstName: '',
    middleName: '',
    familyNameKana: '',
    firstNameKana: '',
    middleNameKana: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',
    phoneNumber: '',
    homePhoneNumber: '',
    postalCode: '',
    prefecture: '',
    city: '',
    streetAddress: '',
    buildingName: '',
    roomNumber: '',
    emailAddress: '',
    residentCardNumber: '',
    myNumber: '',
    maritalStatus: '',
    isWidowed: false,
    isSingleParent: false,
    isWorkingStudent: false,
    disabilityCategory: '',
    emergencyContactName: '',
    emRelationship: '',
    emergencyContactPhone: '',
    emergencyPostalCode: '',
    emergencyPrefecture: '',
    emergencyCity: '',
    emergencyStreetAddress: '',
    emergencyBuildingName: '',
    emergencyRoomNumber: '',
  };
}

function mergeGeneralValues(src?: GeneralProfileValues): GeneralProfileValues {
  return { ...emptyGeneralValues(), ...src };
}

function formatBool(v: boolean | undefined): string {
  if (v === true) return 'はい';
  if (v === false) return 'いいえ';
  return '—';
}

function formatMarital(v: string | undefined): string {
  if (v === 'married') return '既婚';
  if (v === 'not_married') return '未婚';
  return v?.trim() || '—';
}

function formatDisability(v: string | undefined): string {
  if (v === 'none') return 'なし';
  if (v === 'general') return '一般';
  if (v === 'special') return '特別';
  return v?.trim() || '—';
}

function formatRelationship(v: string | undefined): string {
  const map: Record<string, string> = {
    spouse: '配偶者',
    child: '子',
    grandchild: '孫',
    father: '父',
    mother: '母',
  };
  return (v && map[v]) || v?.trim() || '—';
}

function AuditBanner({ by, at }: { by?: string; at?: string }) {
  if (!by && !at) return null;
  return (
    <div className="profile-section-preview__audit">
      管理者による修正: {by || '—'}
      {at ? `（${at.replace('T', ' ').slice(0, 16)}）` : ''}
    </div>
  );
}

function FieldRow({
  label,
  value,
  editing,
  children,
}: {
  label: string;
  value?: string;
  editing?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="profile-section-preview__field">
      <div className="profile-section-preview__field-label">{label}</div>
      <div className="profile-section-preview__field-value">
        {editing ? children : <span>{value?.trim() || '—'}</span>}
      </div>
    </div>
  );
}

function GeneralProfileContent({
  data,
  mode,
  values,
  onChange,
  signatureUrl,
}: {
  data: AdminGeneralProfileResponse;
  mode: ViewMode;
  values: GeneralProfileValues;
  onChange: (patch: Partial<GeneralProfileValues>) => void;
  signatureUrl: string | null;
}) {
  const editing = mode === 'edit';
  const v = editing ? values : mergeGeneralValues(data.values);
  const set = (key: keyof GeneralProfileValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ [key]: e.target.value });
  const setBool = (key: keyof GeneralProfileValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ [key]: e.target.checked });

  return (
    <div className="profile-section-preview__content">
      <AuditBanner by={data.generalInfoAdminCorrectedBy} at={data.generalInfoAdminCorrectedAt} />

      <h3 className="profile-section-preview__section-title">氏名</h3>
      <FieldRow label="姓" value={v.familyName} editing={editing}>
        <input className="profile-section-preview__input" value={v.familyName ?? ''} onChange={set('familyName')} />
      </FieldRow>
      <FieldRow label="ミドルネーム" value={v.middleName} editing={editing}>
        <input className="profile-section-preview__input" value={v.middleName ?? ''} onChange={set('middleName')} />
      </FieldRow>
      <FieldRow label="名" value={v.firstName} editing={editing}>
        <input className="profile-section-preview__input" value={v.firstName ?? ''} onChange={set('firstName')} />
      </FieldRow>
      <FieldRow label="姓（カナ）" value={v.familyNameKana} editing={editing}>
        <input className="profile-section-preview__input" value={v.familyNameKana ?? ''} onChange={set('familyNameKana')} />
      </FieldRow>
      <FieldRow label="ミドルネーム（カナ）" value={v.middleNameKana} editing={editing}>
        <input className="profile-section-preview__input" value={v.middleNameKana ?? ''} onChange={set('middleNameKana')} />
      </FieldRow>
      <FieldRow label="名（カナ）" value={v.firstNameKana} editing={editing}>
        <input className="profile-section-preview__input" value={v.firstNameKana ?? ''} onChange={set('firstNameKana')} />
      </FieldRow>

      <h3 className="profile-section-preview__section-title">基本</h3>
      <FieldRow label="生年月日" value={v.dateOfBirth} editing={editing}>
        <input type="date" className="profile-section-preview__input" value={v.dateOfBirth ?? ''} onChange={set('dateOfBirth')} />
      </FieldRow>
      <FieldRow label="性別" value={v.gender} editing={editing}>
        <input className="profile-section-preview__input" value={v.gender ?? ''} onChange={set('gender')} />
      </FieldRow>
      <FieldRow label="国籍" value={v.nationality} editing={editing}>
        <input className="profile-section-preview__input" value={v.nationality ?? ''} onChange={set('nationality')} />
      </FieldRow>

      <h3 className="profile-section-preview__section-title">連絡先・住所</h3>
      <FieldRow label="携帯電話" value={v.phoneNumber} editing={editing}>
        <input className="profile-section-preview__input" value={v.phoneNumber ?? ''} onChange={set('phoneNumber')} />
      </FieldRow>
      <FieldRow label="自宅電話" value={v.homePhoneNumber} editing={editing}>
        <input className="profile-section-preview__input" value={v.homePhoneNumber ?? ''} onChange={set('homePhoneNumber')} />
      </FieldRow>
      <FieldRow label="メール" value={v.emailAddress} editing={editing}>
        <input className="profile-section-preview__input" value={v.emailAddress ?? ''} onChange={set('emailAddress')} />
      </FieldRow>
      <FieldRow label="郵便番号" value={v.postalCode} editing={editing}>
        <input className="profile-section-preview__input" value={v.postalCode ?? ''} onChange={set('postalCode')} />
      </FieldRow>
      <FieldRow label="都道府県" value={v.prefecture} editing={editing}>
        <input className="profile-section-preview__input" value={v.prefecture ?? ''} onChange={set('prefecture')} />
      </FieldRow>
      <FieldRow label="市区町村" value={v.city} editing={editing}>
        <input className="profile-section-preview__input" value={v.city ?? ''} onChange={set('city')} />
      </FieldRow>
      <FieldRow label="町名・番地" value={v.streetAddress} editing={editing}>
        <input className="profile-section-preview__input" value={v.streetAddress ?? ''} onChange={set('streetAddress')} />
      </FieldRow>
      <FieldRow label="建物名" value={v.buildingName} editing={editing}>
        <input className="profile-section-preview__input" value={v.buildingName ?? ''} onChange={set('buildingName')} />
      </FieldRow>
      <FieldRow label="部屋番号" value={v.roomNumber} editing={editing}>
        <input className="profile-section-preview__input" value={v.roomNumber ?? ''} onChange={set('roomNumber')} />
      </FieldRow>

      <h3 className="profile-section-preview__section-title">在留・マイナンバー</h3>
      <FieldRow label="在留カード番号" value={v.residentCardNumber} editing={editing}>
        <input className="profile-section-preview__input" value={v.residentCardNumber ?? ''} onChange={set('residentCardNumber')} />
      </FieldRow>
      <FieldRow label="マイナンバー" value={v.myNumber} editing={editing}>
        <input className="profile-section-preview__input" value={v.myNumber ?? ''} onChange={set('myNumber')} />
      </FieldRow>

      <h3 className="profile-section-preview__section-title">世帯・障害</h3>
      <FieldRow label="婚姻" value={formatMarital(v.maritalStatus)} editing={editing}>
        <select className="profile-section-preview__input" value={v.maritalStatus ?? ''} onChange={set('maritalStatus')}>
          <option value="">—</option>
          <option value="married">既婚</option>
          <option value="not_married">未婚</option>
        </select>
      </FieldRow>
      <FieldRow label="寡婦・ひとり親" value={formatBool(v.isWidowed)} editing={editing}>
        <label><input type="checkbox" checked={!!v.isWidowed} onChange={setBool('isWidowed')} /> 寡婦</label>
      </FieldRow>
      <FieldRow label="" value={formatBool(v.isSingleParent)} editing={editing}>
        <label><input type="checkbox" checked={!!v.isSingleParent} onChange={setBool('isSingleParent')} /> ひとり親</label>
      </FieldRow>
      <FieldRow label="" value={formatBool(v.isWorkingStudent)} editing={editing}>
        <label><input type="checkbox" checked={!!v.isWorkingStudent} onChange={setBool('isWorkingStudent')} /> 勤労学生</label>
      </FieldRow>
      <FieldRow label="障害者区分" value={formatDisability(v.disabilityCategory)} editing={editing}>
        <select className="profile-section-preview__input" value={v.disabilityCategory ?? ''} onChange={set('disabilityCategory')}>
          <option value="">—</option>
          <option value="none">なし</option>
          <option value="general">一般</option>
          <option value="special">特別</option>
        </select>
      </FieldRow>

      <h3 className="profile-section-preview__section-title">緊急連絡先</h3>
      <FieldRow label="氏名" value={v.emergencyContactName} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyContactName ?? ''} onChange={set('emergencyContactName')} />
      </FieldRow>
      <FieldRow label="続柄" value={v.emRelationship} editing={editing}>
        <input className="profile-section-preview__input" value={v.emRelationship ?? ''} onChange={set('emRelationship')} />
      </FieldRow>
      <FieldRow label="電話" value={v.emergencyContactPhone} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyContactPhone ?? ''} onChange={set('emergencyContactPhone')} />
      </FieldRow>
      <FieldRow label="郵便番号" value={v.emergencyPostalCode} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyPostalCode ?? ''} onChange={set('emergencyPostalCode')} />
      </FieldRow>
      <FieldRow label="都道府県" value={v.emergencyPrefecture} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyPrefecture ?? ''} onChange={set('emergencyPrefecture')} />
      </FieldRow>
      <FieldRow label="市区町村" value={v.emergencyCity} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyCity ?? ''} onChange={set('emergencyCity')} />
      </FieldRow>
      <FieldRow label="町名・番地" value={v.emergencyStreetAddress} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyStreetAddress ?? ''} onChange={set('emergencyStreetAddress')} />
      </FieldRow>
      <FieldRow label="建物名" value={v.emergencyBuildingName} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyBuildingName ?? ''} onChange={set('emergencyBuildingName')} />
      </FieldRow>
      <FieldRow label="部屋番号" value={v.emergencyRoomNumber} editing={editing}>
        <input className="profile-section-preview__input" value={v.emergencyRoomNumber ?? ''} onChange={set('emergencyRoomNumber')} />
      </FieldRow>

      {(data.signatureCaptured || signatureUrl) && (
        <>
          <h3 className="profile-section-preview__section-title">署名</h3>
          {signatureUrl ? (
            <img src={signatureUrl} alt="署名" className="profile-section-preview__signature" />
          ) : (
            <p className="profile-section-preview__muted">署名画像を読み込めませんでした</p>
          )}
        </>
      )}
    </div>
  );
}

function FamilyMemberCard({
  member,
  index,
  editing,
  onChange,
  onRemove,
}: {
  member: FamilyMemberApi;
  index: number;
  editing: boolean;
  onChange: (patch: Partial<FamilyMemberApi>) => void;
  onRemove: () => void;
}) {
  const set = (key: keyof FamilyMemberApi) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const val = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    onChange({ [key]: val });
  };

  return (
    <div className="profile-section-preview__member-card">
      <div className="profile-section-preview__member-header">
        <strong>家族 {index + 1}</strong>
        {editing && (
          <button type="button" className="profile-section-preview__remove-btn" onClick={onRemove}>
            削除
          </button>
        )}
      </div>
      <FieldRow label="続柄" value={formatRelationship(member.relationship)} editing={editing}>
        <select className="profile-section-preview__input" value={member.relationship ?? ''} onChange={set('relationship')}>
          <option value="">—</option>
          <option value="spouse">配偶者</option>
          <option value="child">子</option>
          <option value="grandchild">孫</option>
          <option value="father">父</option>
          <option value="mother">母</option>
        </select>
      </FieldRow>
      <FieldRow label="氏名" value={member.fullName} editing={editing}>
        <input className="profile-section-preview__input" value={member.fullName ?? ''} onChange={set('fullName')} />
      </FieldRow>
      <FieldRow label="氏名（カナ）" value={member.fullNameKana} editing={editing}>
        <input className="profile-section-preview__input" value={member.fullNameKana ?? ''} onChange={set('fullNameKana')} />
      </FieldRow>
      <FieldRow label="生年月日" value={member.dateOfBirth} editing={editing}>
        <input type="date" className="profile-section-preview__input" value={member.dateOfBirth ?? ''} onChange={set('dateOfBirth')} />
      </FieldRow>
      <FieldRow label="マイナンバー" value={member.myNumber} editing={editing}>
        <input className="profile-section-preview__input" value={member.myNumber ?? ''} onChange={set('myNumber')} />
      </FieldRow>
      <FieldRow label="障害者区分" value={formatDisability(member.disabilityCategory)} editing={editing}>
        <select className="profile-section-preview__input" value={member.disabilityCategory ?? ''} onChange={set('disabilityCategory')}>
          <option value="">—</option>
          <option value="none">なし</option>
          <option value="general">一般</option>
          <option value="special">特別</option>
        </select>
      </FieldRow>
      <FieldRow label="同一世帯" value={formatBool(member.livingInSameHousehold)} editing={editing}>
        <label>
          <input
            type="checkbox"
            checked={member.livingInSameHousehold === true}
            onChange={(e) => onChange({ livingInSameHousehold: e.target.checked })}
          />{' '}
          同一世帯
        </label>
      </FieldRow>
      <FieldRow label="別居先住所" value={member.ifNotAddress} editing={editing}>
        <input className="profile-section-preview__input" value={member.ifNotAddress ?? ''} onChange={set('ifNotAddress')} />
      </FieldRow>
      <FieldRow label="非居住者" value={formatBool(member.nonResidentFlag)} editing={editing}>
        <label>
          <input
            type="checkbox"
            checked={member.nonResidentFlag === true}
            onChange={(e) => onChange({ nonResidentFlag: e.target.checked })}
          />{' '}
          国外居住
        </label>
      </FieldRow>
      <FieldRow label="健康保険扶養" value={formatBool(member.healthInsuranceDependent)} editing={editing}>
        <label>
          <input
            type="checkbox"
            checked={member.healthInsuranceDependent === true}
            onChange={(e) => onChange({ healthInsuranceDependent: e.target.checked })}
          />{' '}
          扶養
        </label>
      </FieldRow>
      <FieldRow label="源泉控除対象配偶者" value={formatBool(member.withholdingSpouse)} editing={editing}>
        <label>
          <input
            type="checkbox"
            checked={member.withholdingSpouse === true}
            onChange={(e) => onChange({ withholdingSpouse: e.target.checked })}
          />{' '}
          対象
        </label>
      </FieldRow>
      <FieldRow label="税扶養" value={formatBool(member.claimTaxDependent)} editing={editing}>
        <label>
          <input
            type="checkbox"
            checked={member.claimTaxDependent === true}
            onChange={(e) => onChange({ claimTaxDependent: e.target.checked })}
          />{' '}
          扶養
        </label>
      </FieldRow>
      {member.estimatedIncome != null && (
        <FieldRow label="所得見積" value={String(member.estimatedIncome)} editing={editing}>
          <input
            className="profile-section-preview__input"
            value={member.estimatedIncome ?? ''}
            onChange={(e) => onChange({ estimatedIncome: e.target.value ? Number(e.target.value) : undefined })}
          />
        </FieldRow>
      )}
    </div>
  );
}

function FamilyProfileContent({
  data,
  mode,
  members,
  onMembersChange,
}: {
  data: AdminFamilyProfileResponse;
  mode: ViewMode;
  members: FamilyMemberApi[];
  onMembersChange: (members: FamilyMemberApi[]) => void;
}) {
  const editing = mode === 'edit';
  const displayMembers = editing ? members : data.members ?? [];

  const updateMember = (index: number, patch: Partial<FamilyMemberApi>) => {
    const next = members.map((m, i) => (i === index ? { ...m, ...patch } : m));
    onMembersChange(next);
  };

  const removeMember = (index: number) => {
    onMembersChange(members.filter((_, i) => i !== index));
  };

  const addMember = () => {
    onMembersChange([
      ...members,
      {
        relationship: '',
        fullName: '',
        fullNameKana: '',
        dateOfBirth: '',
        livingInSameHousehold: true,
      },
    ]);
  };

  return (
    <div className="profile-section-preview__content">
      <AuditBanner by={data.familyInfoAdminCorrectedBy} at={data.familyInfoAdminCorrectedAt} />
      {displayMembers.length === 0 ? (
        <p className="profile-section-preview__muted">登録されている家族はありません。</p>
      ) : (
        displayMembers.map((m, i) => (
          <FamilyMemberCard
            key={m.id ?? `new-${i}`}
            member={editing ? members[i] : m}
            index={i}
            editing={editing}
            onChange={(patch) => updateMember(i, patch)}
            onRemove={() => removeMember(i)}
          />
        ))
      )}
      {editing && (
        <button type="button" className="profile-section-preview__add-btn" onClick={addMember}>
          家族を追加
        </button>
      )}
    </div>
  );
}

const ProfileSectionPreview: React.FC<ProfileSectionPreviewProps> = ({
  section,
  employeeEmail,
  employeeName,
  invitationId,
  readOnly = false,
  onClose,
  showResubmitModal,
  resubmitReason,
  onResubmitReasonChange,
  onOpenResubmitModal,
  onCloseResubmitModal,
  onApprove,
  onSaveResubmit,
}) => {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [mode, setMode] = useState<ViewMode>('view');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [generalData, setGeneralData] = useState<AdminGeneralProfileResponse | null>(null);
  const [familyData, setFamilyData] = useState<AdminFamilyProfileResponse | null>(null);
  const [generalEdit, setGeneralEdit] = useState<GeneralProfileValues>(emptyGeneralValues());
  const [familyEdit, setFamilyEdit] = useState<FamilyMemberApi[]>([]);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);

  const reviewDecision: boolean | null =
    section.adminApproved === true ? true : section.adminApproved === false ? false : null;

  const title = section.nameJa ?? section.name;
  const isFamily = section.section === 'family_info';

  const loadData = useCallback(async () => {
    if (!employeeEmail.trim()) {
      setLoadState('error');
      return;
    }
    setLoadState('loading');
    setSaveError(null);
    try {
      if (isFamily) {
        const data = await fetchAdminFamilyInfo(employeeEmail.trim(), invitationId);
        setFamilyData(data);
        setFamilyEdit(data.members ?? []);
      } else {
        const data = await fetchAdminGeneralProfile(employeeEmail.trim(), invitationId);
        setGeneralData(data);
        setGeneralEdit(mergeGeneralValues(data.values));
        const url = await fetchAdminSignaturePreviewUrl(employeeEmail.trim());
        setSignatureUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      }
      setLoadState('ready');
    } catch {
      setLoadState('error');
    }
  }, [employeeEmail, invitationId, isFamily]);

  useEffect(() => {
    void loadData();
    return () => {
      setSignatureUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      if (isFamily) {
        const updated = await updateAdminFamilyInfo({
          email: employeeEmail.trim(),
          invitationId,
          members: familyEdit,
        });
        setFamilyData(updated);
        setFamilyEdit(updated.members ?? []);
      } else {
        const updated = await updateAdminGeneralProfile({
          email: employeeEmail.trim(),
          invitationId,
          values: generalEdit,
        });
        setGeneralData(updated);
        setGeneralEdit(mergeGeneralValues(updated.values));
      }
      setMode('view');
    } catch {
      setSaveError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleModeToggle = () => {
    if (mode === 'edit') {
      if (isFamily && familyData) setFamilyEdit(familyData.members ?? []);
      else if (generalData) setGeneralEdit(mergeGeneralValues(generalData.values));
      setMode('view');
      setSaveError(null);
    } else {
      setMode('edit');
    }
  };

  const handleResubmitClick = () => {
    if (reviewDecision === false) {
      void onSaveResubmit();
      return;
    }
    onOpenResubmitModal();
  };

  return (
    <div className="document-preview" role="dialog" aria-modal="true" aria-labelledby="profile-section-preview-title">
      <div className="document-preview__backdrop" />
      <div className="document-preview__panel">
        <header className="document-preview__header">
          <h2 id="profile-section-preview-title" className="document-preview__title">
            <span className="document-preview__title-doc">{title}</span>
            {employeeName ? ` — ${employeeName}` : ''}
          </h2>
          <div className="document-preview__header-actions">
            {loadState === 'ready' && !readOnly && (
              <button type="button" className="profile-section-preview__mode-btn" onClick={handleModeToggle}>
                {mode === 'view' ? '編集' : '表示'}
              </button>
            )}
            {reviewDecision === false && (
              <img src={rejectIcon} alt="" className="document-preview__header-reject-icon" aria-hidden />
            )}
            <button type="button" className="document-preview__close-btn" onClick={onClose} aria-label="閉じる">
              ×
            </button>
          </div>
        </header>

        <div className="document-preview__body">
          <div className="document-preview__document-area profile-section-preview__scroll">
            {loadState === 'loading' && <div className="document-preview__placeholder">読み込み中...</div>}
            {loadState === 'error' && <div className="document-preview__placeholder">内容を表示できません</div>}
            {loadState === 'ready' && isFamily && familyData && (
              <FamilyProfileContent
                data={familyData}
                mode={mode}
                members={familyEdit}
                onMembersChange={setFamilyEdit}
              />
            )}
            {loadState === 'ready' && !isFamily && generalData && (
              <GeneralProfileContent
                data={generalData}
                mode={mode}
                values={generalEdit}
                onChange={(patch) => setGeneralEdit((prev) => ({ ...prev, ...patch }))}
                signatureUrl={signatureUrl}
              />
            )}
          </div>

          {saveError && <div className="profile-section-preview__save-error">{saveError}</div>}

          {reviewDecision === true && (
            <div className="document-preview__round-comment">
              <div className="document-preview__round-comment-inner document-preview__round-comment-inner--with-status-icon">
                <img src={approveIcon} alt="" className="document-preview__round-comment-approve-icon" aria-hidden />
                <div className="document-preview__round-comment-copy">
                  <div className="document-preview__round-comment-title">承認</div>
                </div>
              </div>
            </div>
          )}

          {reviewDecision === false && section.adminComment != null && section.adminComment.trim() !== '' && (
            <div className="document-preview__round-comment">
              <div className="document-preview__round-comment-inner document-preview__round-comment-inner--with-status-icon">
                <img src={rejectIcon} alt="" className="document-preview__round-comment-reject-icon" aria-hidden />
                <div className="document-preview__round-comment-copy">
                  <div className="document-preview__round-comment-title">コメント</div>
                  <div className="document-preview__round-comment-text">{section.adminComment}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!readOnly && (
        <footer className="document-preview__footer">
          <div className="document-preview__footer-actions">
            {mode === 'edit' && (
              <button
                type="button"
                className="document-preview__approve-btn"
                disabled={saving}
                onClick={() => void handleSave()}
              >
                {saving ? '保存中...' : '保存'}
              </button>
            )}
            {(reviewDecision === null || reviewDecision === true) && (
              <button type="button" className="document-preview__resubmit-btn" onClick={handleResubmitClick}>
                再提出を依頼する
              </button>
            )}
            {(reviewDecision === null || reviewDecision === false) && (
              <button type="button" className="document-preview__approve-btn" onClick={() => void onApprove()}>
                承認する
              </button>
            )}
          </div>
        </footer>
        )}
      </div>

      {showResubmitModal && (
        <div className="document-preview__reverse-confirm" role="dialog" aria-modal="true">
          <div className="document-preview__reverse-confirm-backdrop" onClick={onCloseResubmitModal} aria-hidden />
          <div className="document-preview__reverse-confirm-box">
            <p className="document-preview__reverse-confirm-message">再提出の理由を入力してください</p>
            <textarea
              value={resubmitReason}
              onChange={(e) => onResubmitReasonChange(e.target.value)}
              rows={4}
              style={{ width: '100%', marginBottom: 12, boxSizing: 'border-box' }}
            />
            <div className="document-preview__reverse-confirm-actions">
              <button type="button" className="document-preview__reverse-confirm-cancel" onClick={onCloseResubmitModal}>
                キャンセル
              </button>
              <button type="button" className="document-preview__reverse-confirm-ok" onClick={() => void onSaveResubmit()}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSectionPreview;
